import { match } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { isLineItemInClaims } from '~/utils/claimLineItem';
import { ClaimType } from '~/utils/constants';
import { isItemReturnEligible, isItemWarrantyEligible } from '~/utils/order';
import {
  makeCrewClaimAction,
  makeCrewClaimLoader,
  redirect,
} from '~/utils/routing';
import { crewOrderLineItemSchema } from '~/utils/schemas';
import { getClaimTypeProgramName } from '~/utils/theme';

type CrewContext = Parameters<
  Parameters<typeof makeCrewClaimLoader>[0]
>[0]['context'];
type CrewOrderLineItem = NonNullable<CrewContext['order']>['lineItems'][number];
type AppStore = Parameters<Parameters<typeof makeCrewClaimAction>[0]>[1];

const sumPendingClaimQuantities = (
  pendingLineItemClaims: CrewContext['crewClaim']['claimLineItems'],
) =>
  pendingLineItemClaims.reduce((sums, { lineItem: { id }, quantity = 0 }) => {
    const currentSum = sums.get(id) ?? 0;
    sums.set(id, currentSum + quantity);
    return sums;
  }, new Map<number, number>());

const updateRemainingReturnableQuantity = (
  pendingLineItemClaims: CrewContext['crewClaim']['claimLineItems'],
) => {
  const pendingClaimQuantities = sumPendingClaimQuantities(
    pendingLineItemClaims,
  );

  return (item: CrewOrderLineItem) => {
    const { currentQuantity, id } = item;
    const pendingClaimQuantity = pendingClaimQuantities.get(id) ?? 0;

    return {
      ...item,
      remainingReturnableQuantity: currentQuantity - pendingClaimQuantity,
    } satisfies CrewOrderLineItem;
  };
};

const paramsSchema = z.object({
  idFromPlatform: z.string(),
  claimType: z.nativeEnum(ClaimType),
});

export const { loader, useLoaderData } = makeCrewClaimLoader(
  ({ params, context }) => {
    const { claimType } = paramsSchema.parse(params);
    const {
      crewClaim: { claimLineItems },
      settings: {
        return: { isEnabled: areReturnsEnabled },
      },
      order,
    } = context;

    const lineItems = order.lineItems.map(
      updateRemainingReturnableQuantity(claimLineItems),
    );

    const isEligible =
      claimType === ClaimType.warranty ?
        isItemWarrantyEligible
      : isItemReturnEligible(areReturnsEnabled);

    const eligibleLineItems = lineItems.filter(isEligible);
    const lastActionableDate =
      claimType === ClaimType.warranty ?
        order.warrantyUntil
      : order.returnUntil;

    const claimTypeProgramName = getClaimTypeProgramName(
      context.settings.theme,
      claimType,
    );
    if (!claimTypeProgramName)
      throw new Error('Claim Type Program Name Not Defined');

    return {
      lastActionableDate,
      claimType,
      lineItems: eligibleLineItems,
      pendingClaims: claimLineItems,
      ineligibleItems: lineItems.filter(
        (item) =>
          !isEligible(item) && !isLineItemInClaims(item.id, claimLineItems),
      ),
      claimTypeProgramName,
    };
  },
);

//
// Action
//

export const FormKeys = {
  claimId: 'claimId',
  lineItem: 'lineItem',
  claimType: 'claimType',
} as const;

const deleteSchema = zfd.formData({
  [FormKeys.claimId]: zfd.text(),
});

const handleDelete = (formData: FormData, appStore: AppStore) => {
  const { claimId } = deleteSchema.parse(formData);
  appStore.dispatch(appStore.actions.crewClaim.removeClaimLineItem(claimId));

  return {
    ok: true,
    message: 'Item removed',
  };
};

const postSchema = zfd.formData({
  [FormKeys.claimType]: zfd.text(z.nativeEnum(ClaimType)),
  [FormKeys.lineItem]: zfd.json(crewOrderLineItemSchema),
});

const handlePost = (formData: FormData, appStore: AppStore) => {
  const result = postSchema.safeParse(formData);

  if (!result.success) {
    return new Error('Malformed form data', {
      cause: {
        error: result.error,
        entries: Array.from(formData.entries()),
      },
    });
  }

  const { lineItem } = result.data;

  if (!lineItem) {
    return {
      ok: false,
      message: 'Selected item not found',
    };
  }

  const id = Math.random().toString(36).slice(2);

  appStore.dispatch(
    appStore.actions.draftClaimLineItem.startNewClaim({
      id,
      lineItem,
      quantity: 1,
      unitPrice: lineItem.unitPrice,
      unitTax: lineItem.unitTax,
      grams: lineItem.grams,
      customFields: {},
      comments: null,
      originalStoreOrderLineItemId: lineItem.id,
    }),
  );

  return redirect(`${id}`);
};

export const { action, useActionResult } = makeCrewClaimAction(
  ({ formData, request }, appStore) =>
    match(request.method)
      .with('DELETE', () => handleDelete(formData, appStore))
      .with('POST', () => handlePost(formData, appStore))
      .otherwise(
        () =>
          new Error('Invalid request method', {
            cause: { method: request.method },
          }),
      ),
);
